// PayrollLogin.tsx
import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { LockIcon, UserIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { TwoFactorLogin } from "@/components/auth/TwoFactorLogin";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useToast } from "./ui/use-toast";
import type { ResetPasswordRequest, ResetPasswordResponse } from "@/types/auth";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const PayrollLogin: React.FC = () => {
  const { login, logout, isAuthenticated, verify2FA, twoFactorRequired, clearTwoFactorState, isLoading } = useAuth();
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    const passwordParam = params.get("password");
    const autoLoginParam = params.get("autologin");

    const shouldAutoLogin =
      autoLoginParam === "true" && emailParam && passwordParam;

    // Function to remove sensitive params from URL
    const clearLoginParams = () => {
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete("email");
      currentParams.delete("password");
      currentParams.delete("autologin");
      navigate(location.pathname + "?" + currentParams.toString(), {
        replace: true,
      });
    };

    if (shouldAutoLogin) {
      if (isAuthenticated) {
        // If authenticated but need to auto-login, log out first.
        // The effect will re-run when isAuthenticated becomes false.
        logout();
      } else {
        // Not authenticated, attempt auto-login.
        setIsSubmitting(true);
        setError("");
        login({ email: emailParam, password: passwordParam })
          .then(() => {
            // On success, clear params. useAuth handles navigation.
            clearLoginParams();
          })
          .catch((err) => {
            setError(
              err instanceof Error
                ? err.message
                : "Auto-login failed. Please check the link or log in manually.",
            );
            // Also clear params on failure.
            clearLoginParams();
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      }
    } else if (isAuthenticated) {
      // Not auto-logging, and authenticated: Redirect to dashboard.
      // This handles normal logged-in state AND state after successful auto-login (once params are cleared).
      navigate("/dashboard", { replace: true });
    } else {
      // Not auto-logging, not authenticated: Prefill email if param exists.
      if (emailParam) {
        setUsername(emailParam);
        // Clear the params after prefilling too, just email in this case
        const currentParams = new URLSearchParams(location.search);
        currentParams.delete("email"); // Only remove email if not auto-logging
        currentParams.delete("password");
        currentParams.delete("autologin");
        navigate(location.pathname + "?" + currentParams.toString(), {
          replace: true,
        });
      }
      // No password prefill without autologin=true.
    }
  }, [
    isAuthenticated,
    navigate,
    location.pathname,
    location.search,
    login,
    logout,
  ]); // Added location.pathname

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError("");
    setIsSubmitting(true);

    try {
      await login({ email: username, password });
      // If login succeeds without 2FA, or 2FA is required, the auth state will handle navigation
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Login failed. Please check your credentials and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FAVerification = async (code: string, useBackupCode = false) => {
    try {
      await verify2FA(code, useBackupCode);
      // Success is handled by the auth state
    } catch (err) {
      // Error handling is done in the component via toast
      throw err;
    }
  };

  const handleGoBackFrom2FA = () => {
    clearTwoFactorState();
    setError("");
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isResetting) return;

    setIsResetting(true);
    try {
      const response = await ApiService.request<ResetPasswordResponse>(
        EndpointKeys.REQUEST_PASSWORD_RESET,
        {
          method: "POST",
          body: { email: resetEmail } as ResetPasswordRequest,
          skipAuth: true,
        },
      );

      toast({
        title: "Password Reset Email Sent",
        description: "Please check your email for password reset instructions.",
      });
      setShowResetDialog(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to initiate password reset. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Show 2FA login if required
  if (twoFactorRequired) {
    return (
      <TwoFactorLogin
        onVerify={handle2FAVerification}
        onGoBack={handleGoBackFrom2FA}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Top banner with logo */}
      <div className="w-full h-16 bg-black text-white flex items-center justify-center shadow-md">
        <div className="text-2xl font-bold">Hirelo</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-none shadow-xl">
          <CardHeader className="space-y-1 pb-2">
            <h1 className="text-2xl font-bold text-center text-black">
              Welcome Back
            </h1>
            <p className="text-sm text-center text-muted-foreground">
              Sign in to continue to Hirelo
            </p>
          </CardHeader>

          <CardContent className="pt-3">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="text-muted-foreground w-5 h-5" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isSubmitting}
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 h-auto text-secondary hover:text-secondary/90"
                    onClick={() => setShowResetDialog(true)}
                  >
                    Forgot Password?
                  </Button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockIcon className="text-muted-foreground w-5 h-5" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium"
                disabled={isSubmitting}
                size="lg"
              >
                {isSubmitting ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </CardContent>

          <div className="px-6 pb-2">
            <Separator className="my-4" />
            <div className="text-center text-sm text-muted-foreground">
              <span>New to Hirelo? </span>
              <Button
                variant="link"
                className="p-0 h-auto text-muted-foreground hover:text-secondary/90"
                onClick={() => navigate("/billing")}
              >
                View our plans
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <div className="h-16 bg-muted flex items-center justify-center px-4">
        <p className="text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} Hirelo. All rights reserved.
        </p>
      </div>

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Reset Password</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resetEmail">Email Address</Label>
              <Input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
              <p className="text-sm text-muted-foreground">
                We'll send you a link to reset your password.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowResetDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
                disabled={isResetting}
              >
                {isResetting ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollLogin;
